import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, OperatorFunction, filter, map, mergeMap, switchMap } from 'rxjs';
import { ConnectionType, Tariff, TariffPeriodUnit, TariffsSearchParams } from '../../system/tariffs/tariff.model';
import { LanguageService } from './language.service';
import { UtilsService } from './utils.service';
import { KkmsApiService } from '../modules/kkms/kkms-api.service';
import { KkmType } from '../modules/kkms/kkms.types';
import { PaymentAccountApiService } from '../modules/payment-account/payment-account-api.service';
import { TariffsApiService } from '../modules/tariffs/tariffs-api.service';
import { TariffDTO } from '../modules/tariffs/tariffs.types';
import { BillingStatus, ShiftState } from '../modules/retail-places/retail-paces.types';

export interface TariffParamsToSearch {
  connectionType?: ConnectionType;
  periodPrice?: number;
  periodUnit?: TariffPeriodUnit;
}

@Injectable({
  providedIn: 'root',
})

export class TariffService {
  constructor(
    private tariffsApiService: TariffsApiService,
    private kkmsApiService: KkmsApiService,
    private paymentAccountApiService: PaymentAccountApiService,
    private languageService: LanguageService,
    private utilsService: UtilsService,
  ) {
  }

  getKkmsTariffs(kkmId: number): Observable<Tariff[]> {
    return this.languageService.languageChanges
      .pipe(
        switchMap(() => this.tariffsApiService.fetchKkmsTariffs(kkmId)),
        map(tariffs => this.addBenefitSum(tariffs)),
        map(tariffs => this.utilsService.sortBy(tariffs, 'periodPrice', 'DESC')),
      );
  }

  getPublicTariffs(): Observable<Tariff[]> {
    return this.languageService.languageChanges
      .pipe(
        mergeMap(() => this.tariffsApiService.fetchPublicTariffs()),
        map(tariffs => this.addBenefitSum(tariffs)),
        map(tariffs => this.utilsService.sortBy(tariffs, 'periodPrice', 'DESC')),
      );
  }

  getPublicTariffsByParams(searchParams: TariffsSearchParams): Observable<Tariff[]> {
    const params = this.utilsService.getHttpParams(searchParams);

    return this.languageService.languageChanges
      .pipe(
        mergeMap(() => this.tariffsApiService.fetchPublicTariffsByParams(params)),
        map(tariffs => this.addBenefitSum(tariffs)),
        map(tariffs => this.utilsService.sortBy(tariffs, 'periodPrice', 'DESC')),
      );
  }

  getTariffById(tariffId: number): Observable<Tariff> {
    return this.getPublicTariffs()
      .pipe(
        map(tariffs => tariffs.find(tariff => tariff.id === tariffId)),
        filter(tariff => tariff !== undefined) as OperatorFunction<Tariff | undefined, Tariff>,
      );
  }

  getRequiredSumToActivateDisabledKkms(): Observable<number> {
    return this.kkmsApiService.fetchStatusesCount()
      .pipe(
        // @ts-ignore
        filter(totalCounts => totalCounts.billingStatusCounts.find(total => total.id === BillingStatus.Blocked).count > 0),
        mergeMap(totalCounts => forkJoin([
          this.kkmsApiService.fetchKkmsByStatus({
            billingStatuses: [BillingStatus.Blocked],
            regStatuses: [],
            shiftState: ShiftState.Unknown,
            type: KkmType.UnknownType,
            pagination: {
              from: 0,
              // @ts-ignore
              to: totalCounts.billingStatusCounts.find(total => total.id === BillingStatus.Blocked).count,
            },
          }),
          this.getPublicTariffs(),
          this.paymentAccountApiService.fetchBalance(),
        ])),
        mergeMap(([retailPlaces, tariffs, balance]) => {
          const kkmsByTariffs = {};
          retailPlaces.forEach((retailPlace) => {
            retailPlace.kkms.forEach((kkm) => {
              // @ts-ignore
              kkmsByTariffs[kkm.tariffId] = kkmsByTariffs[kkm.tariffId]
                // @ts-ignore
                ? kkmsByTariffs[kkm.tariffId] + 1
                : 1;
            });
          });

          // @ts-ignore
          const sumToActivateKkms = tariffs.reduce((sum, tariff) => (kkmsByTariffs[tariff.id]
            // @ts-ignore
            ? sum + (tariff.periodPrice * kkmsByTariffs[tariff.id])
            : sum), 0) - balance.amount;

          return of(sumToActivateKkms);
        }),
      );
  }

  getTariffsByParams(tariffs: Tariff[], tariffParams: TariffParamsToSearch): Tariff[] {
    return tariffs.filter((tariff) => {
      const equalConnectionType = tariffParams.connectionType
        ? tariff.connectionType.connection === tariffParams.connectionType
        : true;
      const equalPeriodPrice = tariffParams.periodPrice !== undefined
        ? tariff.periodPrice === tariffParams.periodPrice
        : true;
      const equalPeriodUnit = tariffParams.periodUnit
        ? tariff.periodUnit === tariffParams.periodUnit
        : true;

      return equalConnectionType && equalPeriodPrice && equalPeriodUnit;
    }) || [];
  }

  getTariffPeriodUnitMultiplier(tariff: TariffDTO): number {
    switch (tariff.periodUnit) {
      case 'DAY': return 365;
      case 'MONTH': return 12;
      case 'YEAR': return 1;
      default: return 0;
    }
  }

  private addBenefitSum(tariffs: TariffDTO[]): Tariff[] {
    const maxYearSum = tariffs.reduce((maxSum, tariff) => {
      const tariffYearSum = this.getTariffYearSum(tariff);

      return Math.max(maxSum, tariffYearSum);
    }, 0);

    return tariffs.map(tariff => ({
      ...tariff,
      benefit: maxYearSum - this.getTariffYearSum(tariff),
    }));
  }

  private getTariffYearSum(tariff: TariffDTO): number {
    const multiplier = this.getTariffPeriodUnitMultiplier(tariff);

    return tariff.periodUnit && tariff.periodPrice
      ? tariff.periodPrice * multiplier
      : 0;
  }
}
